// The compile types, ported from SuiteCompile (Preset.swift, Pipeline.swift, Narration.swift) and kept stable for the
// writers built on them (W5b's Typst and screenplay writers among them): add fields, never rename. Optional Swift
// properties are optional here (absent = nil, as Swift's JSONEncoder leaves them out).
import type { Block, FragmentKind, StoryEntity } from '@writing-suite/core';
import type { BookMatter, MergeTagStyle, PublicationDetails } from '@writing-suite/core';

export type { Block, BookMatter, FragmentKind, MergeTagStyle, PublicationDetails };

// MARK: Preset.swift

/** `OutputFormat`. */
export type OutputFormat = 'docx' | 'pdf' | 'epub' | 'markdown' | 'text' | 'audiobook' | 'html' | 'fdx' | 'fountain';
export const OUTPUT_FORMATS: readonly OutputFormat[] = ['docx', 'pdf', 'epub', 'markdown', 'text', 'audiobook', 'html', 'fdx', 'fountain'];

/** `OutputFormat.fileExtension`. */
export function fileExtension(format: OutputFormat): string {
  switch (format) {
    case 'markdown': return 'md';
    case 'text': return 'txt';
    case 'audiobook': return 'm4b';
    default: return format;
  }
}

/** `SectionRole`: the semantic role Map assigns to each selected item. */
export type SectionRole = 'part' | 'chapter' | 'scene' | 'front-matter' | 'back-matter' | 'epigraph' | 'skip';
export const SECTION_ROLES: readonly SectionRole[] = ['part', 'chapter', 'scene', 'front-matter', 'back-matter', 'epigraph', 'skip'];

/** `CompilePreset.Condition`. */
export interface Condition {
  /** A collection's name: a saved search or a hand-picked collection. */
  collection?: string;
  field?: string;
  equals?: string;
  sectionType?: string;
  hasChildren?: boolean;
  /** Some child is itself a folder (or has children of its own). */
  hasSubfolders?: boolean;
  depth?: number;
  kind?: string;
  /** A journal's post (`journalPosts`: a document with no document above it). */
  post?: boolean;
  /** Anything under a journal's post: one of its sections. */
  inPost?: boolean;
}

/** `CompilePreset.Select`. */
export interface Select {
  /** Arrangement name, or `manuscript` for the manuscript arrangement. */
  arrangement: string;
  respectIncludeInCompile: boolean;
  exclude: Condition[];
  include?: Condition[];
  frontMatter: boolean;
  backMatter: boolean;
  /** Compile one Submission document (a `BookMatter` raw value) instead of the manuscript. */
  document?: string;
  /** Journals: the Opening & Sign-off documents around every post. */
  postMatter?: boolean;
  /** Compile in the opposite order at every level. */
  reversed?: boolean;
  /** Compile the one post chosen (`only`) rather than the whole journal. */
  onePost?: boolean;
}

/** `join`: the item's text runs on from the section before it, as part of it (`joining`); a journal's sections join their post. */
export interface MapRule { when: Condition; role: SectionRole; join?: boolean }
export interface MapSettings { rules: MapRule[] }

/** `CompilePreset.Page`. Sizes: `letter`, `a4`, `a5`, or `WxH` inches such as `6x9` (canonical after validation). */
export interface Page {
  size: string;
  /** Inches. */
  margins: number;
  inside?: number;
  outside?: number;
  top?: number;
  bottom?: number;
}

export interface Font { family: string; size: number }

export interface TitlePage {
  include: boolean;
  /** The author's contact block (Shunn). Always off when blind. */
  contact: boolean;
  /** Round the word count to this many words; 0 for exact. */
  wordCountRounding: number;
  wordCount: boolean;
  byline: boolean;
}

/** `CompilePreset.PrintSettings`: a printed interior. */
export interface PrintSettings {
  versoHead: string;
  rectoHead: string;
  chapterOpensRecto: boolean;
  pageEdgeOrnament: string;
  /** `kdp`, `ingramspark` or `none`. */
  platform: string;
  bleed: number;
  signature: number;
  preventWidows: boolean;
  headingScale: number;
  balanceSpreads: boolean;
  /** `x-1a` for PDF/X-1a:2001, else absent. */
  pdfx?: string;
}

/** `CompilePreset.ScreenplaySettings`. */
export interface ScreenplaySettings {
  sceneNumbers: boolean;
  continueds: boolean;
  boldSceneHeadings: boolean;
  /** Blank lines before a scene heading. */
  sceneHeadingSpacing: number;
  /** `ScriptFormat`'s raw value: a film script (absent, 'screen'), a sitcom ('multicam'), a stage play ('stage-us',
   *  'stage-uk') or a radio play ('radio'). */
  format?: string;
  /** A play's page numbers: `act-scene-page` ("II-1-51", the default in acts) or `plain` ("51."). */
  pageNumbers?: string;
  /** A play's "END OF ACT ONE" after every act but the last and "END OF PLAY" at the end. */
  endMarkers?: boolean;
  /** A play's scenes numbered on through the play rather than from 1 in each act. */
  continuousScenes?: boolean;
  /** A sample of the play instead of all of it (a theatre's "first ten pages"). */
  sample?: ScreenplaySample;
  /** Offered only to a musical. */
  musical?: boolean;
  /** A sitcom's page header: the scene's letter under the page number ("(B)"). */
  sceneLetterInHeader?: boolean;
  /** A radio play's speech numbers: 'page' (from 1 on each page) or 'none'. */
  speechNumbers?: string;
}

/** `ScreenplaySettings.Sample`: the first `pages` pages of the play, or from the scene `from` (its title or document id),
 *  ending with the last speech or direction that ends on the last page. */
export interface ScreenplaySample { pages: number; from?: string }

/** `ScreenplaySettings.pageNumberStyles`. */
export const SCREENPLAY_PAGE_NUMBERS: readonly string[] = ['act-scene-page', 'plain'];

/** `CompilePreset.AudioSettings` (Narration.swift). */
export interface AudioSettings {
  matter: string[];
  closingCredits: boolean;
  chapterHeading: string[];
  partHeading: string[];
  afterHeading: number;
  sceneBreak: number;
  paragraph: number;
  leadIn: number;
  tail: number;
}

/** `CompilePreset.RenderSettings`. */
export interface RenderSettings {
  page: Page;
  font: Font;
  /** A multiple: 2 for double. */
  lineSpacing: number;
  /** Inches. */
  firstLineIndent: number;
  /** Points. */
  paragraphSpacing: number;
  justify: boolean;
  sceneBreak: string;
  /** `{surname}`, `{shortTitle}`, `{title}`, `{author}`, `{page}`; empty for none. */
  header: string;
  titlePage: TitlePage;
  /** `{number}`, `{numberWords}`, `{roman}`, `{title}`. */
  partHeading: string[];
  chapterHeading: string[];
  chapterStartsNewPage: boolean;
  /** Inches. */
  chapterDrop: number;
  /** Keep each chapter whole on a page when it fits (a short poem never splits across pages); a longer one breaks as
   *  usual. Print only. */
  keepChaptersWhole?: boolean;
  endMarker: string;
  italicAsUnderline: boolean;
  print?: PrintSettings;
  /** Lines centred at the top of a single-document compile; `{title}`, `{author}`, `{words}`. */
  documentTitle?: string[];
  letterhead?: boolean;
  audio?: AudioSettings;
  screenplay?: ScreenplaySettings;
  /** What links become (`LinkHandling`); unset: kept where the format can hold one, the words alone in print and
   *  scripts. */
  links?: LinkHandling;
  /** Keep the writer's highlights and text colours in the file (a Word copy for an editor, who reads them as notes);
   *  unset or false: left out, as every other format leaves them. Only a Word preset honours it. */
  keepHighlights?: boolean;
}

/** `CompilePreset.LinkHandling` (design-docs/Links — design.md §4): `keep` links where the format has them (EPUB, HTML,
 *  Word, a reading PDF, Markdown), the words elsewhere; `text` the words alone; `footnote` the words with a note giving
 *  the address; `inline` the words followed by the address in parentheses. */
export type LinkHandling = 'keep' | 'text' | 'footnote' | 'inline';
export const LINK_HANDLINGS: readonly LinkHandling[] = ['keep', 'text', 'footnote', 'inline'];

/** `CompilePreset`: resolved (inheritance merged) and validated. */
export interface CompilePreset {
  id: string;
  name: string;
  description: string;
  format: OutputFormat;
  blind: boolean;
  /** A journal's preset: journals see only these (and their own). */
  journal?: boolean;
  /** `paperback`, `hardcover`, `ebook`, `audiobook`; unset, it follows from the format. */
  edition?: string;
  select: Select;
  map: MapSettings;
  render: RenderSettings;
}

// MARK: Pipeline.swift

/** `AuthorInfo`: who the manuscript is by. Ignored when a preset is blind. */
export interface AuthorInfo {
  /** The published name: byline, `{author}`, the ebook's creator, the credits. */
  name: string;
  /** For running headers; defaults to the last word of `name`. */
  surname?: string;
  /** Address, phone, email…, one per line. */
  contact: string[];
  /** The writer's own name when `name` is a pen name (contact blocks and letterheads carry it). */
  legalName?: string;
  /** Co-authors after `name`, in byline order (W9c: the project's `settings.byline`, kept by value). The contact
   *  block stays the owner's; bylines, `{author}`, `{surname}` and the ebook's creators name everyone. */
  coAuthors?: string[];
  /** The word before the last name of a byline ("A and B", "A, B and C"); "and" by default. */
  joiner?: string;
  /** A musical's credits by role (`Byline.roles`: each name's, the published name's under ""). */
  roles?: Record<string, string[]>;
  /** True when `name` is a pen name with contact details of its own: `contact` is the pen name's and the contact block
   *  gives the pen name, so the writer's own name appears nowhere. Absent otherwise. */
  ownNameWithheld?: boolean;
}

/** `PenName` (SuiteModel/PenName.swift). `contact`: the name's own contact details, one per line (absent = the
 *  writer's own name and details; empty = its own, none given yet). */
export interface PenName { id: string; name: string; surname?: string; contact?: string[] }

/** `AudiobookSettings` (SuiteModel). */
export interface AudiobookSettings {
  narrator: string;
  engine: 'system' | 'elevenlabs';
  systemVoice?: string;
  elevenLabsVoiceID?: string;
  elevenLabsVoiceName?: string;
  elevenLabsModel?: string;
  /** Written → spoken. */
  pronunciations: Record<string, string>;
}

/** `CompiledSection`: one item of the role-tagged stream Map produces. */
export interface CompiledSection {
  role: SectionRole;
  title: string;
  /** 1-based among sections of the same role. */
  number: number;
  fragmentID: string | null;
  blocks: Block[];
  /** A scene following another scene in the same chapter: a scene break goes before it. */
  followsScene: boolean;
  /** Fragments whose text was joined onto this section's (a journal post's sections, `MapRule.join`), in order. */
  joined?: string[];
  /** Which front/back-matter page (or Submission document) this is. */
  matter: BookMatter | null;
  /** The fragment's text as written (screenplays set its Fountain). */
  source: string | null;
  kind: FragmentKind | null;
  /** False for a chapter or part that takes no number (a prologue): the counters pass it by and `heading` drops the
   *  lines that would show one. Absent = numbered (as the Swift's default). */
  numbered?: boolean;
}

/** `CompiledManuscript`. Derived values (`titlePageContent`, `wordCountPhrase`, `isDocument`…) are functions in pipeline.ts. */
export interface CompiledManuscript {
  title: string;
  shortTitle: string;
  language: string;
  projectID: string;
  sections: CompiledSection[];
  wordCount: number;
  author: AuthorInfo | null;
  preset: CompilePreset;
  /** A single-document compile's manuscript length (for `{words}`); null otherwise. */
  manuscriptWordCount: number | null;
  audiobook: AudiobookSettings;
  publication: PublicationDetails;
  mergeTags: MergeTagStyle[];
  /** Journal posts numbered only for this output, by fragment id. */
  provisionalNumbers: Record<string, number>;
  /** Journal posts here carrying their own opening and sign-off, filled in place (absent = none). */
  ownPostMatter?: string[];
  postNumberProblems: { shared: number[]; missing: number[] };
  /** A beta reader's copy: "Reading copy for Anna Ng · …". Null otherwise. */
  readerMark: string | null;
  /** The cast's characters, for a play's cast page (absent = none). */
  characters?: StoryEntity[];
  /** A musical (`CompileSource.musical`): songs numbered, a Musical Numbers page, credits by role. */
  musical?: boolean;
  /** The whole play's act count ("A Play in Two Acts") when this is a preview's sample (`sample`), which keeps only
   *  the opening scenes; absent = count the sections here. */
  playActs?: number;
  /** The source carried highlights or text colours; they're still in the sections only when `keepsMarks` (a Word copy
   *  that keeps them). Absent = none. */
  hasMarks?: boolean;
}

/** `TitlePageContent`: the preset's title page. */
export interface TitlePageContent {
  contact: string[];
  wordCount: string | null;
  title: string;
  byline: string | null;
}
